import { debounce, Theme } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import TextField from '@material-ui/core/TextField'
import Autocomplete, {
    AutocompleteCloseReason,
} from '@material-ui/lab/Autocomplete'
import { NoteTypeReasonOption } from 'bpm/pages/ProductsPage/ProductDetailsPage/components/ListingNotes/ListingNotes.types'
import React, { ChangeEvent, useCallback, useState } from 'react'

const autoCompleteStyles = makeStyles((theme: Theme) => ({
    chipLabel: {
        overflow: 'unset',
        textOverflow: 'unset',
        whiteSpace: 'normal',
        maxWidth: 200,
    },
    option: {
        color: '#5E5F61',
    },
    groupLabel: {
        color: '#212529',
    },
}))

export interface AutoCompleteFilterProps {
    /** Name of this autocomplete. Used to create its id. */
    name: string

    /** The items the user can choose from. */
    items: NoteTypeReasonOption[]

    /** Hint text for textbox. */
    placeholder?: string

    /** Fired when user selects or removes an item. */
    onChange?: (
        event: ChangeEvent<{}>,
        /** The selected item. */
        value: NoteTypeReasonOption
    ) => void

    /** Fired shortly after the autocomplete input text field changes. */
    onInputChangeDebounced?: (
        /** The value of the input text field. */
        value: string
    ) => void

    /** Fired when user opens the drop-down (e.g., by clicking the textbox,
     * clicking the arrow button, or typing in the textbox when the drop-down is closed). */
    onOpen?: (event: ChangeEvent<{}>) => void

    /** Fired when the user closes the drop-down (e.g., by clicking the arrow button or hitting Esc.) */
    onClose?: (event: ChangeEvent<{}>) => void

    /** Item to show as selected**/
    selectedItem?: any

    /** Reference element for input textbox. */
    onInputRef?: (inputElement: HTMLElement) => void

    /** If true, show a loading state for the drop-down selection listbox. */
    loading?: boolean

    /** If true, show custom format for option. */
    customFormat?: boolean
}

const AutoCompleteNotesFilter: React.FC<AutoCompleteFilterProps> = ({
    name,
    items,
    placeholder,
    onChange,
    onInputChangeDebounced,
    onOpen,
    onClose,
    selectedItem,
    onInputRef,
    loading = false,
    customFormat = false,
    ...props
}) => {
    const styles = autoCompleteStyles()
    let onTextInputRef = onInputRef
    const [open, setOpen] = useState(false)
    const [inputValue, setInputValue] = React.useState('')
    const [loadingOverride, setLoadingOverride] = useState<boolean>(false)
    const selectedValue = selectedItem
    const debouncedRaiseOnInput = useCallback(
        debounce((newValue: string) => {
            onInputChangeDebounced?.(newValue)
            setLoadingOverride(false) // we can now wait on the parent component to set 'loading' (e.g., from a useQuery)
        }),
        []
    )

    const handleOpen = useCallback(
        (e) => {
            e.preventDefault()
            setOpen(true)
            onOpen?.(e)
        },
        [onOpen]
    )

    const handleClose = useCallback(
        (e: ChangeEvent<{}>, reason: AutocompleteCloseReason) => {
            e.preventDefault()

            // This prevents closing the dropdown when the tree loads since accordiontree(not a button) takes focus when the tree loads
            if (
                reason === 'blur' && // @ts-expect-error relatedTarget not in changeevent
                e.relatedTarget?.type !== 'button' && // @ts-expect-error relatedTarget not in changeevent
                !e.relatedTarget?.className.includes('Mui') // for closing the select dropdown while retaining the popover open
            ) {
                ;(e.target as HTMLInputElement).focus()
                return
            }

            setOpen(false)

            // This is a flag for the blur to retain the inputvalue. We may need to revisit this if we don't want to clear the input value on blur
            if ((props as any).clearOnBlur === false) {
                setInputValue('')
            }

            onClose?.(e)
        },
        [onClose]
    )

    const handleInputChange = useCallback((_unusedEvent, newInputValue) => {
        // show 'Loading...' as soon as user presses a key; this avoids showing a 'No Results' briefly before 'loading' state
        // is refreshed by parent component (e.g., from a useQuery call)
        setLoadingOverride(true)
        setInputValue(newInputValue)
        debouncedRaiseOnInput(newInputValue)
    }, [])

    const handleFilterOptions = useCallback(
        (options, { inputValue }) =>
            options.filter(
                (option: NoteTypeReasonOption) =>
                    // To be included in the drop-down list, an option can't already be selected...
                    selectedValue?.reason !== option.reason &&
                    // ...AND it must contain the input search string, if any.
                    (!inputValue ||
                        option.reason
                            .toLocaleLowerCase()
                            .includes(inputValue.toLocaleLowerCase()))
            ),
        [selectedValue]
    )

    const renderInput = useCallback(
        (params) => {
            const { InputProps, ...restParams } = params
            const { startAdornment, ...restInputProps } = InputProps
            return (
                <>
                    {/* for test cases */}
                    <input
                        style={{ display: 'none' }}
                        type="text"
                        name={`fake-${name}`}
                    />
                    <TextField
                        {...restParams}
                        inputProps={{
                            ...params.inputProps,
                            'aria-autocomplete': 'none',
                            autoComplete: 'off',
                        }}
                        InputProps={{
                            ...restInputProps,
                            startAdornment: (
                                <div
                                    style={{
                                        maxHeight: 266,
                                        overflowY: 'auto', // show vertical scrollbar when many items selected
                                    }}
                                >
                                    {startAdornment}
                                </div>
                            ),
                        }}
                        id={`${name}-input`}
                        data-testid={`${name}-input-test`}
                        name={`input${name}`}
                        label="SELECT"
                        variant="outlined"
                        inputRef={(input) =>
                            onTextInputRef && onTextInputRef(input)
                        }
                        placeholder="Search Note Reasons"
                    />
                </>
            )
        },
        [name, placeholder, onTextInputRef, selectedValue]
    )

    return (
        <Autocomplete<NoteTypeReasonOption, false, true, false>
            {...props}
            autoHighlight
            disableClearable
            id={`${name}-autocomplete`}
            ListboxProps={{ style: { maxHeight: 260 } }}
            loading={loading || loadingOverride}
            onClose={handleClose}
            onOpen={handleOpen}
            open={open}
            renderInput={renderInput}
            options={items}
            onChange={onChange}
            onInputChange={handleInputChange}
            groupBy={(option) => option.noteType}
            getOptionLabel={(option) => option?.reason ?? ''}
            classes={{ option: styles.option, groupLabel: styles.groupLabel }}
            value={selectedValue || null}
            getOptionSelected={(option, value) =>
                option?.reason === value?.reason &&
                option?.noteType === value?.noteType
            }
        />
    )
}

export default AutoCompleteNotesFilter
